import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip, Box, Typography, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  formatSpiralBadge, formatNearTermBadge, describeZoneTiming,
  spiralZoneColor, SPIRAL_MAX_ZONES,
} from '../utils/spiralOverlays';

/**
 * The chip that reports what a Spiral Reversal Scan found, floating over the chart.
 *
 * <p>Renders nothing at all when there is no zone. "No convergence found" is a real answer, but it
 * belongs in the messages panel rather than as a permanent chip over the price action - a badge
 * that is always present stops being read.
 *
 * <p>The tooltip carries the evidence: which pivots contributed, at which Fibonacci step, and how
 * many days that step spans. That detail is the difference between a number a reader trusts and one
 * they cannot check, and it is why the chip is hoverable rather than just informative.
 *
 * <p>It is dismissable. The chip sits over the top-left of the price action, which is exactly where
 * the oldest bars are, and until now the only way to clear it was to reload the chart — a scan
 * whose answer you had already read went on covering the data it was about.
 */
export default function SpiralScanBadge({ zone, zones, params, truncated, undrawn, anchored, onClick, onDismiss }) {
  if (!zone) return null;

  // A set of near-term zones reads differently from a single upcoming one: the lead zone's timing
  // relative to now is the headline, because a convergence that has already passed can be checked
  // against the bars. Falls back to the original single-zone wording when no set was supplied.
  const zoneList = Array.isArray(zones) && zones.length > 0 ? zones : null;
  // When the window was anchored on a clicked bar, every distance is reported from THAT bar.
  // "8025d ago" is true of today and says nothing about the question asked, which was what sits
  // near the turn the reader clicked.
  const label = zoneList ? formatNearTermBadge(zoneList, anchored) : formatSpiralBadge(zone);
  const hits = Array.isArray(zone.hits) ? zone.hits : [];

  // One row per PIVOT. A pivot can land in the zone at more than one Fibonacci step, and listing
  // it twice would suggest two independent turns where there is one.
  const byPivot = [];
  const seen = new Set();
  hits.forEach((h) => {
    if (seen.has(h.pivotBarIndex)) return;
    seen.add(h.pivotBarIndex);
    byPivot.push(h);
  });

  const tooltip = (
    <Box sx={{ p: 0.5, maxWidth: 340 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Φ convergence · {zone.targetDate}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, mb: 0.5 }}>
        {zone.convergenceCount} pivots · score {Math.round(zone.totalWeightScore ?? 0)}
        {params?.periodName ? ` · ${params.periodName} month` : ''}
        {params?.scoring ? ` · ${params.scoring} weighting` : ''}
      </Typography>

      {/* Said plainly rather than left to be inferred from a band that stops at the chart edge. */}
      {truncated && (
        <Typography variant="caption" sx={{ display: 'block', color: '#ffcc80', mb: 0.5 }}>
          The zone extends past the last loaded bar — the band shows only the visible part.
        </Typography>
      )}

      {/* The legend. With several bands on the chart the colour is the only thing tying a set of
          pivots to its date, so the tooltip has to name the mapping or the colours mean nothing. */}
      {zoneList && zoneList.length > 1 && (
        <>
          <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.25)' }} />
          {zoneList.slice(0, SPIRAL_MAX_ZONES).map((z, i) => (
            <Box key={z.targetBarIndex ?? i} sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 12 }}>
              <span style={{
                width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                backgroundColor: spiralZoneColor(i).border,
              }} />
              <span style={{ fontFamily: 'monospace', minWidth: 76 }}>{z.targetDate}</span>
              <span style={{ opacity: 0.8 }}>{z.convergenceCount}p</span>
              <span style={{ opacity: 0.65 }}>{describeZoneTiming(z, anchored)}</span>
            </Box>
          ))}
          {undrawn > 0 && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mt: 0.5 }}>
              {undrawn} weaker zone{undrawn === 1 ? '' : 's'} in the window are not drawn.
            </Typography>
          )}
        </>
      )}

      <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.25)' }} />

      {byPivot.map((h) => (
        <Box
          key={h.pivotBarIndex}
          sx={{ display: 'flex', gap: 1, alignItems: 'baseline', fontFamily: 'monospace', fontSize: 12 }}
        >
          <span style={{ minWidth: 76 }}>{h.pivotDate}</span>
          <span style={{ minWidth: 34, color: h.pivotType === 'HIGH' ? '#ef9a9a' : '#a5d6a7' }}>
            {h.pivotType === 'HIGH' ? 'HIGH' : 'LOW'}
          </span>
          <span style={{ minWidth: 46 }}>F({h.fibNumber})</span>
          {/* The lunar-month delay in days: the actual span the projection reaches across, which is
              what makes an F-number mean something. */}
          <span style={{ opacity: 0.75 }}>+{Math.round(h.delayDays)}d</span>
        </Box>
      ))}

      {byPivot.length === 0 && (
        <Typography variant="caption">No contributing pivots were returned.</Typography>
      )}
    </Box>
  );

  return (
    <Tooltip arrow placement="bottom-start" title={tooltip}>
      <Chip
        label={`⚡ ${label}`}
        onClick={onClick}
        // MUI renders the × for onDelete, and stops the click from reaching onClick — so dismissing
        // does not also fire the inspection the rest of the chip runs. Omitted entirely when no
        // handler is supplied, rather than shown and inert.
        onDelete={onDismiss}
        deleteIcon={onDismiss ? <CloseIcon aria-label="Dismiss Φ badge" /> : undefined}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          left: 12,
          // Above the chart canvas. Without this the badge is painted over by the candlesticks,
          // which is the same fault the toolbar's z-index fixes.
          zIndex: 5,
          fontWeight: 700,
          color: '#3e2723',
          backgroundColor: 'rgba(255, 193, 7, 0.92)',
          border: '1px solid rgba(245, 124, 0, 0.9)',
          boxShadow: '0 0 10px rgba(255, 152, 0, 0.55)',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': { backgroundColor: 'rgba(255, 179, 0, 0.98)' },
          // The × inherits the chip's dark text rather than MUI's default grey, which is close to
          // unreadable on this amber.
          '& .MuiChip-deleteIcon': {
            color: 'rgba(62, 39, 35, 0.7)',
            '&:hover': { color: '#3e2723' },
          },
        }}
      />
    </Tooltip>
  );
}

SpiralScanBadge.propTypes = {
  /** True when the window was centred on a clicked bar, so timings read from it rather than now. */
  anchored: PropTypes.bool,
  /** The convergence zone to report. Null renders nothing. */
  zone: PropTypes.object,
  /** The scan's echoed parameters, for naming the period and weighting in the tooltip. */
  params: PropTypes.object,
  /** True when the zone runs past the last loaded bar, so the band is only partly drawn. */
  truncated: PropTypes.bool,
  /** All near-term zones drawn, for the colour legend. Falls back to single-zone wording if absent. */
  zones: PropTypes.array,
  /** How many zones the palette could not take, so the tooltip can say so rather than stay silent. */
  undrawn: PropTypes.number,
  onClick: PropTypes.func,
  /**
   * Hides the badge. Absent means no × is drawn at all.
   *
   * Dismissing removes the CHIP, not the study: the bands and pivot markers stay on the chart,
   * because they are the result and the chip is the summary of it. Clearing the drawing is what
   * the toolbar is for, and conflating the two would make the × destructive in a way its icon does
   * not advertise.
   */
  onDismiss: PropTypes.func,
};
